/*
 * OFFSET: Instant variant feedback for the product page + sticky add-to-cart bar.
 *
 * Why this exists:
 *   The Impact theme updates the buy form and the sticky "add to cart" tab only
 *   AFTER an async section-render fetch resolves (see variant-picker.selectCombination,
 *   which `await`s the network render before setting the form variant id, swapping the
 *   image, the price, and dispatching `variant:change`). Behind the local dev proxy that
 *   fetch is ~300ms-1s, so the chosen colour takes a visible beat to appear in the tab.
 *
 * What this does:
 *   On the RAW option `change` (capture phase, so it runs before the theme's async
 *   handler) it resolves the selected variant from already-loaded client-side product
 *   data and immediately:
 *     1. sets every cart form's `input[name="id"]` (so Add-to-Cart is always correct,
 *        even if the shopper clicks the button before the re-render finishes),
 *     2. updates the sticky tab total price (format-preserving, so it matches the theme),
 *     3. toggles the sold-out / disabled state on the submit buttons,
 *     4. swaps the sticky tab thumbnail to the variant image.
 *
 *   The theme's own section re-render still runs afterwards and remains the source of
 *   truth — this only front-runs it so there is no perceptible delay. Everything is
 *   defensive: if data isn't ready or an element is missing, it does nothing and the
 *   normal theme flow takes over.
 */
(function () {
  'use strict';

  var cache = {}; // handle -> variants[]  ;  'p:'+handle -> Promise

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function loadVariants(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (cache['p:' + handle]) return cache['p:' + handle];
    var p = fetch(root() + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.variants) { cache[handle] = d.variants; return d.variants; }
        return null;
      })
      .catch(function () { return null; });
    cache['p:' + handle] = p;
    return p;
  }

  function pickerForForm(form) {
    var id = form.getAttribute('id');
    if (id) {
      var esc = (window.CSS && CSS.escape) ? CSS.escape(id) : id;
      var vp = document.querySelector('variant-picker[form-id="' + esc + '"]');
      if (vp) return vp;
    }
    return form.querySelector ? form.querySelector('variant-picker') : null;
  }

  // Form-associated controls matching a selector. Option radios + buy buttons live
  // OUTSIDE the <form> and attach via the `form=` attribute, so they are reachable
  // through form.elements (not form.querySelectorAll, which only sees descendants).
  function controls(form, selector) {
    var out = [];
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      if (els[i].matches && els[i].matches(selector)) out.push(els[i]);
    }
    return out;
  }

  // The visible, ordered option values currently selected (by option position).
  function selectedTexts(form) {
    var checked = controls(form, 'input[data-option-position]:checked').sort(function (a, b) {
      return (parseInt(a.getAttribute('data-option-position'), 10) || 0) -
             (parseInt(b.getAttribute('data-option-position'), 10) || 0);
    });
    return checked.map(function (inp) {
      var lbl = inp.nextElementSibling;
      var text = '';
      if (lbl) {
        var span = lbl.querySelector(
          '.vcircle__label, .custom-color-swatch__label, .block-swatch__label, .thumbnail-swatch__label'
        );
        text = (span ? span.textContent : lbl.textContent) || '';
      }
      return text.trim();
    });
  }

  function findVariant(variants, texts) {
    if (!variants || !texts.length) return null;
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      if (opts.length !== texts.length) continue;
      var match = true;
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]).trim() !== texts[j]) { match = false; break; }
      }
      if (match) return variants[i];
    }
    return null;
  }

  function applyVariant(form, variant) {
    if (!variant) return;

    // 1. cart form variant id — makes Add-to-Cart reflect the chosen colour instantly
    //    (the most important fix: the right variant is added even if the shopper hits
    //    the button before the theme's section re-render finishes).
    var idInput = form.elements.namedItem('id') || form.querySelector('input[name="id"]');
    if (idInput && 'value' in idInput && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
    }

    // NOTE: price is intentionally NOT touched here. A third-party pricing app (Spark)
    // overrides the displayed price, so product.js prices do not match what the shopper
    // should see. Leave price to the theme / Spark re-render to avoid showing a wrong price.

    // 2. sold-out / disabled state on submit buttons tied to this form
    var soldOut = variant.available === false;
    var submits = controls(form, 'button[type="submit"]');
    for (var s = 0; s < submits.length; s++) {
      submits[s].toggleAttribute('disabled', soldOut);
      submits[s].setAttribute('aria-disabled', soldOut ? 'true' : 'false');
    }

    // 3. sticky tab thumbnail (images are not affected by the pricing app)
    if (variant.featured_image && variant.featured_image.src) {
      var imgs = document.querySelectorAll(
        '.offset-main-product-sticky-bar variant-media img, product-quick-add variant-media img'
      );
      for (var k = 0; k < imgs.length; k++) {
        imgs[k].removeAttribute('srcset');
        imgs[k].src = variant.featured_image.src;
      }
    }
  }

  function handleChange(input) {
    if (!input || !input.matches || !input.matches('input[data-option-position]')) return;
    try {
      var form = input.form;
      if (!form && input.getAttribute('form')) form = document.getElementById(input.getAttribute('form'));
      if (!form) return;
      var picker = pickerForForm(form);
      var handle = picker && picker.getAttribute('handle');
      if (!handle) return;
      var texts = selectedTexts(form);
      var ready = cache[handle];
      if (ready) {
        applyVariant(form, findVariant(ready, texts)); // synchronous — zero delay
      } else {
        loadVariants(handle).then(function (variants) {
          applyVariant(form, findVariant(variants, texts));
        });
      }
    } catch (e) {
      /* never let an enhancement break the page; the theme's own flow still runs */
    }
  }

  document.addEventListener('change', function (e) { handleChange(e.target); }, true);

  // Warm the product data the moment a shopper touches an option (belt-and-braces).
  document.addEventListener('pointerdown', function (e) {
    var hit = e.target.closest && e.target.closest('.variant-picker__option-values label, .vcircle');
    if (!hit) return;
    var vp = hit.closest('variant-picker');
    var handle = vp && vp.getAttribute('handle');
    if (handle) loadVariants(handle);
  }, true);

  // Eagerly warm product data shortly after load so even the FIRST colour change
  // resolves synchronously (no fetch wait). Only runs for multi-variant products,
  // since single-variant products render no <variant-picker>.
  function warmAll() {
    var pickers = document.querySelectorAll('variant-picker[handle]');
    for (var i = 0; i < pickers.length; i++) loadVariants(pickers[i].getAttribute('handle'));
  }
  // Start the warm immediately (deferred one task so it doesn't block first paint).
  // requestIdleCallback is unreliable here — it gets starved by heavy third-party
  // scripts, leaving the first colour change un-warmed. A direct timer is dependable.
  function schedule(fn) {
    setTimeout(fn, 0);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { schedule(warmAll); });
  } else {
    schedule(warmAll);
  }
})();
